import axios from 'axios';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { oryLoginWithProvider } from '@revelio/auth';
import { validateEmail } from '@revelio/iso-utility';

import { environment } from '../../environment';
import { SurfContext } from '../../surf-context';
import { ThemeHeading, ThemeLink, ThemeTagline } from './theme/theme';
import { Button, Field, Stack, TextInput, uiStyles } from './ui';

function LoginSSOForm() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const csrfToken = useContext(SurfContext);
  // Carried onto the back-to-login link so `?theme=` survives the hop.
  const { search } = useLocation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ email: string }>();

  const validateAndLogin = async ({ email }: { email: string }) => {
    setError('');
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !validateEmail(trimmedEmail)) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${environment.PUBLIC_USER_MGMT_URL}/api/users/validate-sso-support`,
        { email: trimmedEmail },
        {
          headers: {
            'x-surf-token': csrfToken,
          },
          withCredentials: true,
          xsrfCookieName: 'x-surf-token',
          xsrfHeaderName: 'x-surf-token',
        }
      );
      if (data?.supported) {
        await oryLoginWithProvider(data.provider);
      } else {
        setError(
          "Enterprise SSO login isn't enabled for your email. You can log in with your password or create a new account."
        );
      }
    } catch {
      setError('Failed to start SSO authentication.');
    }
    setLoading(false);
  };

  return (
    <Stack gap="var(--ap-sso-gap)" fullWidth>
      <Stack gap="12px">
        <ThemeHeading>Log in with SSO</ThemeHeading>
        <ThemeTagline>
          Enter your work email and we'll route you to your organization's
          single sign-on
        </ThemeTagline>
      </Stack>

      <form onSubmit={handleSubmit(validateAndLogin)}>
        <Stack gap="16px">
          <Field error={errors['email']?.message}>
            <TextInput
              type="email"
              placeholder="Email"
              aria-label="Email"
              autoComplete="username"
              disabled={loading}
              invalid={!!errors['email']}
              {...register('email', {
                required: 'Email is required',
                validate: {
                  emailSyntax: (value: string) =>
                    validateEmail(value.trim()) || 'Valid email is required',
                },
              })}
            />
          </Field>
          <Button style={{ marginTop: '8px' }} disabled={loading} type="submit">
            Log in with SSO
          </Button>
        </Stack>
        {error && (
          <p className={uiStyles.formError}>
            {error} Interested in using SSO?{' '}
            <ThemeLink href={`${environment.PUBLIC_WEBSITE_URL}/contact`}>
              Contact Sales
            </ThemeLink>
          </p>
        )}
      </form>

      <ThemeLink href={`/login${search}`} className={uiStyles.selfStart}>
        Log in with email and password instead
      </ThemeLink>
    </Stack>
  );
}

export default LoginSSOForm;
